import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, Lock, FileText, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { getCategoryById, getDocumentById } from '../data/documents';
import { generatePartialPreviewHtml, formatDateFr } from '../utils/previewUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

// Stripe Payment Link
const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/3cI28q4NOcW936FcvOgjC04";

export default function DocumentPage() {
  const { documentId } = useParams();
  const [searchParams] = useSearchParams();
  
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [currentStep, setCurrentStep] = useState(1); // 1: form, 2: preview
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  
  // Load document data from local data
  useEffect(() => {
    const doc = getDocumentById(documentId);
    if (doc) {
      setDocument(doc);
      
      // Initialize form data with empty values
      const initialData = {};
      doc.fields.forEach(field => {
        initialData[field.name] = '';
      });
      setFormData(initialData);
    } else {
      setError('Document non trouvé');
    }
    setLoading(false);
  }, [documentId, searchParams]);
  
  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const validateForm = () => {
    if (!document) return false;
    
    for (const field of document.fields) {
      if (field.required && !formData[field.name]) {
        return false;
      }
    }
    return true;
  };
  
  // Generate PARTIAL preview HTML locally (truncated for security)
  const generatePreviewHtml = () => {
    const nom = formData.nom || 'NOM';
    const prenom = formData.prenom || 'Prénom';
    const adresse_ligne1 = formData.adresse_ligne1 || '';
    const adresse_ligne2 = formData.adresse_ligne2 || '';
    const code_postal = formData.code_postal || '';
    const ville = formData.ville || '';
    const lieu = formData.lieu_signature || formData.lieu || '';
    const content = formData.objet_attestation || formData.description || formData.contenu || formData.situation || formData.motivation || formData.explication || '[Contenu du document]';
    
    const addressText = [adresse_ligne1, adresse_ligne2, `${code_postal} ${ville}`.trim()].filter(Boolean).join(', ');
    const introText = `Je soussigné(e) ${prenom} ${nom.toUpperCase()}, demeurant au ${addressText},`;
    
    return generatePartialPreviewHtml({
      nom,
      prenom,
      adresse_ligne1,
      adresse_ligne2,
      code_postal,
      ville,
      dateStr: formatDateFr(formData.date_document || new Date()),
      lieu,
      title: document?.name || 'Document',
      content,
      introText
    });
  };

  const handleGeneratePreview = () => {
    if (!validateForm()) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setPreviewLoading(true);
    setError(null);
    
    // Generate preview locally
    setTimeout(() => {
      const html = generatePreviewHtml();
      setPreviewHtml(html);
      setCurrentStep(2);
      setPreviewLoading(false);
    }, 500);
  };
  
  const handlePayment = () => {
    setPaymentLoading(true);
    
    // Save form data to localStorage for PDF generation after payment
    const paymentData = {
      documentId: documentId,
      documentName: document?.name,
      formData: formData,
      previewHtml: previewHtml,
      timestamp: Date.now()
    };
    localStorage.setItem('pdf_facile_payment', JSON.stringify(paymentData));
    
    // Redirect to Stripe Payment Link
    window.location.href = STRIPE_PAYMENT_LINK;
  };
  
  const category = document ? getCategoryById(document.category_id) : null;
  
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header showBack backTo="/" />
        <main className="flex-1 flex items-center justify-center">
          <div className="spinner" />
        </main>
      </div>
    );
  }
  
  if (error && !document) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header showBack backTo="/" />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-4">{error}</h1>
            <Link to="/" className="btn-primary inline-block">
              Retour à l'accueil
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header showBack backTo={category ? `/categorie/${category.id}` : '/'} />
      
      <main className="flex-1">
        {/* Document Header */}
        <section className="bg-slate-50 py-6 md:py-8">
          <div className="container-app">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-4 overflow-x-auto whitespace-nowrap">
              <Link to="/" className="hover:text-primary">Accueil</Link>
              <ChevronRight className="w-4 h-4 flex-shrink-0" />
              {category && (
                <>
                  <Link to={`/categorie/${category.id}`} className="hover:text-primary">{category.name}</Link>
                  <ChevronRight className="w-4 h-4 flex-shrink-0" />
                </>
              )}
              <span className="text-foreground font-medium truncate">{document?.name}</span>
            </nav>
            
            <h1 className="text-xl sm:text-2xl font-bold text-primary mb-2" data-testid="document-title">
              {document?.name}
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base">
              {document?.description}
            </p>
          </div>
        </section>
        
        {/* Step Indicator */}
        <section className="py-4 border-b border-slate-200">
          <div className="container-app">
            <div className="flex items-center justify-center gap-2 sm:gap-4">
              <div className="flex items-center gap-2">
                <div className={`step-dot ${currentStep >= 1 ? (currentStep > 1 ? 'completed' : 'active') : 'inactive'}`}>
                  {currentStep > 1 ? <Check className="w-4 h-4" /> : '1'}
                </div>
                <span className="text-sm font-medium hidden sm:inline">Formulaire</span>
              </div>
              <div className={`step-line flex-1 max-w-[60px] ${currentStep > 1 ? 'completed' : ''}`} />
              <div className="flex items-center gap-2">
                <div className={`step-dot ${currentStep >= 2 ? (currentStep > 2 ? 'completed' : 'active') : 'inactive'}`}>
                  {currentStep > 2 ? <Check className="w-4 h-4" /> : '2'}
                </div>
                <span className="text-sm font-medium hidden sm:inline">Aperçu</span>
              </div>
              <div className={`step-line flex-1 max-w-[60px] ${currentStep > 2 ? 'completed' : ''}`} />
              <div className="flex items-center gap-2">
                <div className={`step-dot ${currentStep >= 3 ? 'active' : 'inactive'}`}>
                  3
                </div>
                <span className="text-sm font-medium hidden sm:inline">Téléchargement</span>
              </div>
            </div>
          </div>
        </section>
        
        {/* Content */}
        <section className="section-spacing">
          <div className="container-app max-w-2xl">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
            
            {/* Step 1: Form */}
            {currentStep === 1 && (
              <div data-testid="form-step">
                <h2 className="text-lg font-semibold mb-6">Complétez les informations</h2>
                
                <div className="space-y-5">
                  {document?.fields.map((field) => (
                    <div key={field.name} className="form-field">
                      <Label htmlFor={field.name} className="label-field">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      
                      {field.type === 'text' && (
                        <Input
                          id={field.name}
                          type="text"
                          placeholder={field.placeholder || ''}
                          value={formData[field.name] || ''}
                          onChange={(e) => handleInputChange(field.name, e.target.value)}
                          className="input-field"
                          data-testid={`field-${field.name}`}
                        />
                      )}
                      
                      {field.type === 'date' && (
                        <Input
                          id={field.name}
                          type="date"
                          value={formData[field.name] || ''}
                          onChange={(e) => handleInputChange(field.name, e.target.value)}
                          className="input-field"
                          data-testid={`field-${field.name}`}
                        />
                      )}
                      
                      {field.type === 'textarea' && (
                        <Textarea
                          id={field.name}
                          placeholder={field.placeholder || ''}
                          value={formData[field.name] || ''}
                          onChange={(e) => handleInputChange(field.name, e.target.value)}
                          className="input-field min-h-[100px] py-3"
                          data-testid={`field-${field.name}`}
                        />
                      )}
                      
                      {field.type === 'select' && (
                        <Select
                          value={formData[field.name] || ''}
                          onValueChange={(value) => handleInputChange(field.name, value)}
                        >
                          <SelectTrigger className="input-field" data-testid={`field-${field.name}`}>
                            <SelectValue placeholder="Sélectionnez une option" />
                          </SelectTrigger>
                          <SelectContent>
                            {field.options?.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="mt-8">
                  <Button
                    onClick={handleGeneratePreview}
                    disabled={!validateForm() || previewLoading}
                    className="btn-primary w-full"
                    data-testid="generate-preview-btn"
                  >
                    {previewLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Génération en cours...
                      </>
                    ) : (
                      <>
                        <FileText className="w-5 h-5 mr-2" />
                        Générer l'aperçu gratuit
                      </>
                    )}
                  </Button>
                  
                  {/* Réassurance sous le bouton */}
                  <div className="mt-6 text-xs text-slate-500 space-y-1">
                    <p>• Documents conformes aux usages administratifs français</p>
                    <p>• Aperçu partiel gratuit avant paiement</p>
                    <p>• Téléchargement immédiat après paiement</p>
                    <p>• Aucune inscription requise</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 2: Preview */}
            {currentStep === 2 && previewHtml && (
              <div data-testid="preview-step">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold">Aperçu du document</h2>
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="text-sm text-accent hover:underline"
                  >
                    Modifier
                  </button>
                </div>
                
                {/* Preview */}
                <div 
                  className="preview-document mb-6"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
                
                {/* Payment CTA */}
                <div className="bg-slate-50 rounded-lg p-6 text-center">
                  <div className="payment-badge inline-flex mb-4">
                    <Lock className="w-4 h-4" />
                    <span>Paiement sécurisé</span>
                  </div>
                  
                  <h3 className="text-lg font-semibold mb-2">
                    Téléchargez votre document PDF
                  </h3>
                  <p className="text-muted-foreground text-sm mb-6">
                    Le PDF sera identique à l'aperçu ci-dessus, au format A4.
                  </p>
                  
                  <Button
                    onClick={handlePayment}
                    disabled={paymentLoading}
                    className="btn-primary w-full"
                    data-testid="pay-button"
                  >
                    {paymentLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Redirection...
                      </>
                    ) : (
                      <>
                        <Lock className="w-5 h-5 mr-2" />
                        Télécharger le PDF – 4,99 €
                      </>
                    )}
                  </Button>
                  
                  <p className="text-xs text-muted-foreground mt-4">
                    Paiement unique • Sans abonnement • Téléchargement immédiat
                  </p>
                  
                  <p className="text-xs text-slate-400 mt-3">
                    Paiement sécurisé via Stripe – transactions protégées par chiffrement SSL
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
